import { Directive, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core'

/**
 * Apply an arbitrary `Record<string, string | null | undefined>` to the
 * host element as HTML attributes. Useful when a generic component
 * needs to forward a set of `data-*` hooks supplied by a consumer
 * without baking each attribute name into the template.
 *
 * Falsy values (null/undefined/empty string) remove the attribute, so
 * the directive is reactively driven by the input.
 *
 *     <tr [appAttrs]="{ 'data-station-id': stationId, 'data-tone': tone }">
 */
@Directive({
    selector: '[appAttrs]',
    standalone: true
})
export class AppAttrsDirective implements OnChanges {
    @Input('appAttrs') attrs: Record<string, string | null | undefined> | null | undefined = null

    private appliedKeys = new Set<string>()

    constructor(private host: ElementRef<HTMLElement>) {}

    ngOnChanges(_changes: SimpleChanges): void {
        const el = this.host.nativeElement
        const next = this.attrs ?? {}
        // Remove keys we previously set but are no longer in the map.
        for (const key of this.appliedKeys) {
            if (!(key in next)) el.removeAttribute(key)
        }
        const newKeys = new Set<string>()
        for (const [key, value] of Object.entries(next)) {
            if (value == null || value === '') {
                el.removeAttribute(key)
            } else {
                el.setAttribute(key, value)
                newKeys.add(key)
            }
        }
        this.appliedKeys = newKeys
    }
}
